import { useState } from 'react'

import { type Subject, isArchived, totalOf } from '../../api/subjects'
import { Button } from '../../ui/Control'
import { Icon } from '../../ui/Icon'

/**
 * The subjects somebody has, running ones first.
 *
 * **The archived ones are behind a fold**, not on a screen of their own and not gone: archiving is
 * "that is over", never "that never happened". A term that has finished stops taking up room in the
 * daily view and stays one click away, complete.
 *
 * Every row says how much is under it, per kind. Those counts travel with the list rather than being
 * fetched per row, which is what makes this one request.
 */
export interface SubjectListProps {
  readonly subjects: readonly Subject[]
  readonly openId: string | null
  readonly onOpen: (id: string) => void
}

export function SubjectList({ subjects, openId, onOpen }: SubjectListProps) {
  const [showArchived, setShowArchived] = useState(false)
  const running = subjects.filter((subject) => !isArchived(subject))
  const archived = subjects.filter(isArchived)

  return (
    <>
      {running.length > 0 && (
        <ul className="subjects__list">
          {running.map((subject) => (
            <SubjectRow
              key={subject.id}
              subject={subject}
              open={subject.id === openId}
              onOpen={() => {
                onOpen(subject.id)
              }}
            />
          ))}
        </ul>
      )}

      {archived.length > 0 && (
        <section className="subjects__archived">
          <Button
            tone="bare"
            icon={showArchived ? 'collapse' : 'expand'}
            aria-expanded={showArchived}
            onClick={() => {
              setShowArchived((before) => !before)
            }}
          >
            {`Archived (${String(archived.length)})`}
          </Button>
          {showArchived && (
            <ul className="subjects__list subjects__list--archived">
              {archived.map((subject) => (
                <SubjectRow
                  key={subject.id}
                  subject={subject}
                  open={subject.id === openId}
                  onOpen={() => {
                    onOpen(subject.id)
                  }}
                />
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  )
}

function SubjectRow({
  subject,
  open,
  onOpen,
}: {
  readonly subject: Subject
  readonly open: boolean
  readonly onOpen: () => void
}) {
  return (
    <li className="subjects__row">
      <button type="button" className="subjects__open" data-open={open} onClick={onOpen}>
        <span className="subjects__mark" aria-hidden="true">
          <Icon name="subject" />
        </span>
        <span className="subjects__what">
          <span className="subjects__name">{subject.name}</span>
          {subject.description !== '' && (
            <span className="subjects__about">{subject.description}</span>
          )}
          <span className="subjects__facts">
            {countsOf(subject).map((count) => (
              <span key={count}>{count}</span>
            ))}
            {subject.folders.length > 0 && (
              <span>{`${String(subject.folders.length)} folder${subject.folders.length === 1 ? '' : 's'}`}</span>
            )}
            {isArchived(subject) && <span className="subjects__archived-mark">Archived</span>}
          </span>
        </span>
      </button>
    </li>
  )
}

/**
 * What is under a subject, in words, and only the kinds that have anything.
 *
 * A row of six zeroes says nothing anybody needs; "14 documents · 3 cards" says what this subject
 * currently is.
 */
function countsOf(subject: Subject): string[] {
  if (totalOf(subject.counts) === 0) {
    return ['Nothing in it yet']
  }
  const said: string[] = []
  const say = (how: number, one: string, many: string) => {
    if (how > 0) {
      said.push(`${String(how)} ${how === 1 ? one : many}`)
    }
  }
  say(subject.counts.documents, 'document', 'documents')
  say(subject.counts.notes, 'note', 'notes')
  say(subject.counts.tasks, 'task', 'tasks')
  say(subject.counts.flashcards, 'card', 'cards')
  say(subject.counts.boards, 'board', 'boards')
  say(subject.counts.appointments, 'appointment', 'appointments')
  return said
}
